/**
 * SubmissionFeedback — Learner-facing grading feedback view.
 *
 * Shows:
 *   - Overall score and percentage
 *   - Per-criterion scores with level descriptions
 *   - Coach feedback text
 *   - Submission history (if multiple attempts)
 */
import React, { useEffect, useState } from 'react';

export default function SubmissionFeedback({ submissionId, onClose, onResubmit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!submissionId) return;
    setLoading(true);
    const token = localStorage.getItem('scenario_os_token');
    fetch(`/scenario-os/coach/rubrics/submissions/${encodeURIComponent(submissionId)}/feedback`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
      .then((r) => (r.ok ? r.json() : r.json().then((d) => Promise.reject(d))))
      .then(setData)
      .catch((e) => setError(e.detail || e.message || 'Failed to load feedback'))
      .finally(() => setLoading(false));
  }, [submissionId]);

  if (loading) return <div style={containerStyle}><div style={mutedStyle}>Loading feedback…</div></div>;
  if (error) return <div style={containerStyle}><div style={errorStyle}>{error}</div></div>;
  if (!data) return null;

  const scores = data.scores || data.ai_scores || [];
  const rubric = data.rubric || {};
  const criteria = rubric.criteria || [];
  const overallPct = data.overall_pct || data.ai_overall_pct || 0;
  const coachOverallPct = data.coach_overall_pct ?? data.coach_score_override ?? null;
  const maxScore = rubric.max_score || 0;
  const statusLabel = {
    pending: 'Pending',
    ai_graded: 'AI Graded',
    reviewed: 'Reviewed',
    coach_approved: 'Coach Approved',
    resubmit_requested: 'Revision Requested',
    coach_review_pending: 'Pending Coach Review',
  }[data.status] || data.status;

  const scoreColor = overallPct >= 80 ? '#059669' : overallPct >= 60 ? '#d97706' : '#dc2626';
  const coachScoreColor = coachOverallPct != null
    ? (coachOverallPct >= 80 ? '#059669' : coachOverallPct >= 60 ? '#d97706' : '#dc2626')
    : '#059669';

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, color: '#1f2937' }}>
            {data.scene_title || 'Submission Feedback'}
          </h2>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Status: <span style={statusBadgeStyle(data.status)}>{statusLabel}</span>
            {data.graded_at && (
              <span style={{ marginLeft: 8 }}>
                Graded: {new Date(data.graded_at).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} style={closeBtnStyle}>✕</button>
        )}
      </div>

      {/* Status banners */}
      {data.status === 'resubmit_requested' && (
        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <strong>Revision Requested</strong>
          {data.coach_note && <p style={{ margin: '8px 0 12px', fontSize: 14, lineHeight: 1.5 }}>{data.coach_note}</p>}
          <button
            onClick={() => onResubmit && onResubmit(data)}
            style={{ background: '#ffc107', border: 'none', borderRadius: 6, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
          >
            Resubmit Project
          </button>
        </div>
      )}
      {data.status === 'coach_review_pending' && (
        <div style={{ background: '#d1ecf1', border: '1px solid #17a2b8', borderRadius: 8, padding: 16, marginBottom: 16 }}>
          <strong>Under Coach Review</strong>
          <p style={{ margin: '6px 0 0', fontSize: 14 }}>Your AI-graded submission is being reviewed by your coach.</p>
        </div>
      )}

      {/* Score cards — AI score and optional coach score side by side */}
      <div style={{ ...scoreCardStyle, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
          <div style={scoreRingStyle(scoreColor)}>
            <span style={{ fontSize: 24, fontWeight: 800, color: scoreColor }}>{overallPct}%</span>
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937' }}>AI Score</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {maxScore > 0 && `out of ${maxScore} points`}
            </div>
          </div>
        </div>
        {coachOverallPct != null && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1 }}>
            <div style={scoreRingStyle(coachScoreColor)}>
              <span style={{ fontSize: 24, fontWeight: 800, color: coachScoreColor }}>{coachOverallPct}%</span>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937' }}>Coach Score</div>
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                {maxScore > 0 && `out of ${maxScore} points`}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Original submission */}
      {(data.text || data.file_url) && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={sectionTitleStyle}>Your Submission</h3>
          {data.text ? (
            <div style={{ ...feedbackBoxStyle, background: '#f8fafc', borderColor: '#e2e8f0' }}>
              <div style={{ fontSize: 14, color: '#1f2937', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {data.text}
              </div>
            </div>
          ) : null}
          {data.file_url ? (
            <a href={data.file_url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: '#4f46e5' }}>
              Open attached file
            </a>
          ) : null}
        </div>
      )}

      {/* AI narrative feedback */}
      {(data.ai_feedback_text || data.ai_feedback) && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={sectionTitleStyle}>AI Feedback</h3>
          <div style={{ ...feedbackBoxStyle, background: '#f0f9ff', borderColor: '#bae6fd' }}>
            <div style={{ fontSize: 14, color: '#0c4a6e', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {data.ai_feedback_text
                || (typeof data.ai_feedback === 'string'
                  ? data.ai_feedback
                  : [
                      data.ai_feedback?.strengths && `Strengths: ${data.ai_feedback.strengths}`,
                      data.ai_feedback?.improvements && `Areas for improvement: ${data.ai_feedback.improvements}`,
                      data.ai_feedback?.encouragement,
                    ].filter(Boolean).join('\n\n'))}
            </div>
          </div>
        </div>
      )}

      {/* Per-criterion scores */}
      {scores.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h3 style={sectionTitleStyle}>Score Breakdown</h3>
          {scores.map((s, i) => {
            const criterion = criteria.find((c) => c.name === s.criterion_name) || {};
            const levels = criterion.levels || [];
            const matchedLevel = levels.find((l) => l.score === s.score);

            return (
              <div key={i} style={criterionRowStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#1f2937' }}>{s.criterion_name}</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: scoreColor }}>
                    {s.score} / {Math.max(...levels.map((l) => l.score), s.score)}
                  </span>
                </div>
                {matchedLevel && (
                  <div style={{ fontSize: 12, color: '#4f46e5', fontWeight: 600, marginBottom: 2 }}>
                    {matchedLevel.label}
                    {matchedLevel.description && (
                      <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 6 }}>
                        — {matchedLevel.description}
                      </span>
                    )}
                  </div>
                )}
                {s.feedback && (
                  <div style={{ fontSize: 13, color: '#374151', marginTop: 4, lineHeight: 1.5 }}>
                    {s.feedback}
                  </div>
                )}
                {s.reasoning && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, fontStyle: 'italic' }}>
                    {s.reasoning}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Coach feedback */}
      {(data.coach_feedback || data.overall_feedback || data.coach_note) && (
        <div style={feedbackBoxStyle}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1e40af', marginBottom: 6 }}>
            Coach Feedback
          </div>
          <div style={{ fontSize: 14, color: '#1f2937', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {data.coach_feedback || data.overall_feedback || data.coach_note}
          </div>
        </div>
      )}

      {/* Submission history */}
      {data.history && data.history.length > 1 && (
        <div style={{ marginTop: 20 }}>
          <h3 style={sectionTitleStyle}>Submission History</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.history.map((h) => (
              <div
                key={h.submission_id}
                style={{
                  ...historyRowStyle,
                  background: h.submission_id === submissionId ? '#eff6ff' : '#fff',
                  borderColor: h.submission_id === submissionId ? '#93c5fd' : '#e5e7eb',
                }}
              >
                <span style={{ fontSize: 13, color: '#374151' }}>
                  {new Date(h.submitted_at).toLocaleDateString()}{' '}
                  {new Date(h.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span style={statusBadgeStyle(h.status)}>
                  {h.status}
                </span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
                  {h.overall_pct || h.ai_overall_pct || '—'}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────

const containerStyle = {
  fontFamily: 'system-ui, -apple-system, sans-serif',
  maxWidth: 680,
  margin: '0 auto',
  padding: 20,
};
const mutedStyle = { color: '#6b7280', fontSize: 14, padding: '24px 0' };
const errorStyle = {
  color: '#b3261e', background: 'rgba(239,68,68,0.08)',
  border: '1px solid rgba(239,68,68,0.3)', padding: 12, borderRadius: 6, fontSize: 14,
};
const closeBtnStyle = {
  background: 'none', border: 'none', fontSize: 18, color: '#6b7280', cursor: 'pointer', padding: 4,
};
const scoreCardStyle = {
  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
  border: '1px solid #e2e8f0', borderRadius: 12,
  padding: 20, marginBottom: 20,
};
const scoreRingStyle = (color) => ({
  width: 80, height: 80, borderRadius: '50%',
  border: `4px solid ${color}`, display: 'flex',
  alignItems: 'center', justifyContent: 'center',
  background: '#fff',
});
const sectionTitleStyle = {
  fontSize: 14, fontWeight: 700, color: '#475569',
  textTransform: 'uppercase', letterSpacing: 0.5,
  marginBottom: 10, marginTop: 0,
};
const criterionRowStyle = {
  background: '#fff', border: '1px solid #f1f5f9', borderRadius: 8,
  padding: 12, marginBottom: 6,
};
const feedbackBoxStyle = {
  background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
  padding: 16,
};
const historyRowStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 6,
};

function statusBadgeStyle(status) {
  const colors = {
    pending: { bg: '#fef3c7', fg: '#92400e' },
    ai_graded: { bg: '#dbeafe', fg: '#1e40af' },
    reviewed: { bg: '#dcfce7', fg: '#166534' },
    resubmit_requested: { bg: '#fff3cd', fg: '#856404' },
    coach_review_pending: { bg: '#d1ecf1', fg: '#0c5460' },
  };
  const c = colors[status] || colors.pending;
  return {
    background: c.bg, color: c.fg, fontSize: 11, fontWeight: 600,
    padding: '2px 8px', borderRadius: 999,
  };
}
